import joy from '@/assets/mascot/dofi-joy.webp';
import proud from '@/assets/mascot/dofi-proud.webp';

// ════════════════════════════════════════════════════════════════
// Общее для карточек, которые уходят наружу: фон, Дофи, подвал со ссылкой.
// Карточка достижения (achievementCard.ts) и разбор недели (weekCard.ts)
// рисуются одной машинкой, чтобы в ленте друга выглядеть одним брендом.
// ════════════════════════════════════════════════════════════════

export const W = 1080;
// 9:16 под сторис (решение Славы 04.09.2026): в 4:5 Telegram добавлял
// чёрные полосы сверху и снизу, карточка выглядела вставленной.
export const H = 1920;
// Верх и низ сторис закрыты интерфейсом Telegram — содержимое держим
// в середине, иначе ссылка уедет под поле ответа.
export const SAFE_TOP = 260;
export const SAFE_BOTTOM = Math.round(H * 0.85);
export const BG = '#0B0F12';
export const EMERALD = '#10E58E';
export const INK = '#F5F7FA';
export const INK_MUTED = '#8B98A5';
export const INK_DIM = '#5A6573';
export const PAD_X = 120;

export const BOT_USERNAME = 'kokorineatbot';

export function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const probe = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(probe).width > maxWidth && cur) {
      lines.push(cur);
      cur = w;
    } else cur = probe;
  }
  if (cur) lines.push(cur);
  return lines;
}

export async function newCard(): Promise<{ canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D }> {
  if (document.fonts?.ready) await document.fonts.ready;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // Фон, отсветы, виньетка, двойная рамка — как у стиха
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);
  const g1 = ctx.createRadialGradient(W * 0.2, H * 0.1, 0, W * 0.2, H * 0.1, W * 1.2);
  g1.addColorStop(0, 'rgba(16,229,142,0.07)');
  g1.addColorStop(1, 'transparent');
  ctx.fillStyle = g1; ctx.fillRect(0, 0, W, H);
  const g2 = ctx.createRadialGradient(W * 0.85, H * 0.95, 0, W * 0.85, H * 0.95, W);
  g2.addColorStop(0, 'rgba(61,216,255,0.05)');
  g2.addColorStop(1, 'transparent');
  ctx.fillStyle = g2; ctx.fillRect(0, 0, W, H);
  const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.3, W / 2, H / 2, H * 0.75);
  vig.addColorStop(0, 'transparent');
  vig.addColorStop(1, 'rgba(0,0,0,0.45)');
  ctx.fillStyle = vig; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(255,255,255,0.13)'; ctx.lineWidth = 2; ctx.strokeRect(44, 44, W - 88, H - 88);
  ctx.strokeStyle = 'rgba(16,229,142,0.22)'; ctx.lineWidth = 1; ctx.strokeRect(58, 58, W - 116, H - 116);

  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(16,229,142,0.75)';
  ctx.font = '500 26px Inter, system-ui, sans-serif';
  ctx.letterSpacing = '8px';
  ctx.fillText('SLAVA HEALTH', W / 2, SAFE_TOP);
  ctx.letterSpacing = '0px';
  return { canvas, ctx };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`не загрузилась картинка ${src}`));
    img.src = src;
  });
}

/**
 * Дофи на карточке — как в окне достижения (просьба Славы 07.10.2026: «чтобы
 * на картинке был Дофи, как когда выходит оповещение»). Сверху мягкое свечение,
 * справа снизу — значок достижения в кружке, тоже как в окне.
 * Картинка не загрузилась — рисуем только значок: карточка без маскота лучше,
 * чем несостоявшийся репост.
 */
export async function drawDofi(
  ctx: CanvasRenderingContext2D,
  opts: { cy: number; size: number; pose?: 'joy' | 'proud'; badge?: string | null },
): Promise<void> {
  const { cy, size } = opts;
  const glow = ctx.createRadialGradient(W / 2, cy, 0, W / 2, cy, size * 0.75);
  glow.addColorStop(0, 'rgba(16,229,142,0.20)');
  glow.addColorStop(1, 'transparent');
  ctx.fillStyle = glow;
  ctx.fillRect(W / 2 - size, cy - size, size * 2, size * 2);

  try {
    const img = await loadImage(opts.pose === 'proud' ? proud : joy);
    ctx.drawImage(img, W / 2 - size / 2, cy - size / 2, size, size);
  } catch (e) {
    console.warn('[card] Дофи не нарисован:', e);
  }

  if (opts.badge) {
    const r = size * 0.17;
    const bx = W / 2 + size * 0.36;
    const by = cy + size * 0.36;
    ctx.fillStyle = '#1A2128';
    ctx.beginPath(); ctx.arc(bx, by, r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.14)'; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = INK;
    ctx.font = `${Math.round(r * 1.15)}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.fillText(opts.badge, bx, by + 2);
    ctx.textBaseline = 'alphabetic';
  }
}

/** Линия, строка про приложение, ссылка и реф-код. Возвращает низ последней строки. */
export function drawFooter(ctx: CanvasRenderingContext2D, y: number, refCode?: string | null): number {
  ctx.textAlign = 'center';
  ctx.strokeStyle = 'rgba(255,255,255,0.14)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(PAD_X, y); ctx.lineTo(W - PAD_X, y); ctx.stroke();
  y += 56;
  ctx.fillStyle = INK_MUTED;
  ctx.font = '400 30px Inter, system-ui, sans-serif';
  ctx.fillText('Еда по фото · КБЖУ · ИИ-помощник Дофи, который помнит тебя', W / 2, y);
  y += 60;
  ctx.fillStyle = EMERALD;
  ctx.font = '600 36px Inter, system-ui, sans-serif';
  ctx.fillText(`t.me/${BOT_USERNAME}`, W / 2, y);
  if (refCode) {
    y += 44;
    ctx.fillStyle = INK_DIM;
    ctx.font = '400 26px Inter, system-ui, sans-serif';
    ctx.fillText(`по ссылке друга — код ${refCode}`, W / 2, y);
  }
  if (y > SAFE_BOTTOM) console.warn('[card] низ уходит под интерфейс сторис:', y, '>', SAFE_BOTTOM);
  return y;
}

export function toPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas пуст'))), 'image/png', 0.95),
  );
}
